import type { SignatureData, Stroke } from "./types";
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, INK } from "./types";
import { drawAll, widthFor } from "./render";

/** Compact, like the original captures — this file ships in a public/ dir. */
export function toJSON(data: SignatureData): string {
  return JSON.stringify(data);
}

/**
 * A static SVG of the finished signature: one path per stroke, stroke width taken
 * from the stroke's mean pressure. `currentColor` so it inherits wherever it lands.
 */
export function toSVG(strokes: Stroke[]): string {
  const paths = strokes
    .filter((stroke) => stroke.points.length >= 2)
    .map((stroke) => {
      const d = stroke.points
        .map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`)
        .join(" ");
      const mean =
        stroke.points.reduce((sum, p) => sum + p.pressure, 0) / stroke.points.length;
      return `  <path d="${d}" stroke-width="${widthFor(mean).toFixed(2)}" />`;
    })
    .join("\n");

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CAPTURE_WIDTH} ${CAPTURE_HEIGHT}" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
${paths}
</svg>
`;
}

/** Transparent PNG at 2x, so it stays sharp dropped into a CV or an email footer. */
export async function toPNG(strokes: Stroke[], scale = 2): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = CAPTURE_WIDTH * scale;
  canvas.height = CAPTURE_HEIGHT * scale;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is unavailable in this browser");

  ctx.scale(scale, scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = INK;
  drawAll(ctx, strokes, { paper: false });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to encode PNG"))),
      "image/png",
    );
  });
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function downloadText(text: string, filename: string, type: string): void {
  download(new Blob([text], { type }), filename);
}
